import { Trans } from '@lingui/react';
import { DisplayIdentity } from '@startuptools/business-ui/identity/Identity';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { GqlMeeting, GqlMeetingParticipant, GqlMpKind } from '../../../../../graphql/base-types.graphql';
import { useCreateMeetingParticipantMutation, useMpSetFunctionsMutation } from './FunctionSelect.graphql';
import { ShareholdersMeetingDocument } from '../../../../shareholders-meetings/ShareholdersMeeting.graphql';
import { FunctionKind } from '../types';
import { identityToInput } from '@startuptools/business-ui/identity/identity-to-input';
import { SelectIdentity } from '@startuptools/business-ui/identity/select/SelectIdentity';
import { FormControl } from '@startuptools/ui/joy/FormControl';
import { Controller, useForm } from 'react-hook-form';
import * as React from 'react';
import { IconButton } from '@startuptools/ui/joy/IconButton';
import AddIcon from '@mui/icons-material/Add';
import { Box } from '@startuptools/ui/joy/Box';
import { useProtocolEdit } from '../../ProtocolEditContext';
import { JuridicalKind } from '@startuptools/business-ui/identity/types';

type Mp = (Pick<GqlMeetingParticipant, 'id' | 'present' | 'chairman' | 'secretary' | 'adjuster'> & {
  identity: DisplayIdentity;
  proxy?: DisplayIdentity | null;
})[];

const createRoleOptions = (meetingParticipants: Mp): (DisplayIdentity & { value: string })[] => {
  return meetingParticipants
    .filter(
      mp =>
        mp.identity.juridicalKind === JuridicalKind.Person ||
        (mp.identity.juridicalKind === JuridicalKind.Company && mp.proxy),
    )
    .map(mp => ({
      ...(mp.proxy ?? mp.identity),
      value: mp.id,
    }))
    .reduce(
      (acc, curr) => {
        if (!acc.find(item => item.id === curr.id)) {
          acc.push(curr as ReturnType<typeof createRoleOptions>[number]);
        }
        return acc;
      },
      [] as ReturnType<typeof createRoleOptions>,
    );
};

export const FunctionSelect = ({
  kind,
  meeting,
  value,
  title,
  multiple,
  placeholder,
}: {
  kind: 'chairman' | 'secretary' | 'adjuster';
  meeting: Pick<GqlMeeting, 'id' | 'companyId'> & {
    meetingParticipants: Mp;
  };

  title: React.ReactNode;
  value: string | string[] | undefined;
  multiple?: boolean;
  placeholder: React.ReactNode;
}) => {
  const form = useForm<{ mpId: string | string[] }>({
    defaultValues: {
      mpId: value || '',
    },
  });

  const { setLoading } = useProtocolEdit();

  const [doSetFunction, { loading }] = useMpSetFunctionsMutation({ refetchQueries: [ShareholdersMeetingDocument] });

  const [doCreateMp, { loading: createLoading }] = useCreateMeetingParticipantMutation({
    refetchQueries: [ShareholdersMeetingDocument],
  });

  React.useEffect(() => {
    setLoading(loading || createLoading);
  }, [loading, createLoading, setLoading]);

  const onSubmit = React.useCallback(
    async (data: { mpId: string | string[] }) => {
      await doSetFunction({
        variables: {
          companyId: meeting.companyId,
          input: { ids: Array.isArray(data.mpId) ? data.mpId : [data.mpId], [kind]: true },
        },
      });
    },
    [doSetFunction, meeting.companyId, kind],
  );

  const onDeselect = React.useCallback(
    async (value: string | string[]) => {
      await doSetFunction({
        variables: {
          companyId: meeting.companyId,
          input: { ids: Array.isArray(value) ? value : [value], [kind]: false },
        },
      });
    },
    [doSetFunction, meeting.companyId, kind],
  );

  return (
    <Stack gap={1} width="300px">
      <Typography level="title-md">{title}</Typography>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Controller
          control={form.control}
          name="mpId"
          rules={{ required: true }}
          render={({ field: { value, ...field }, formState }) => (
            <FormControl error={Boolean(formState.errors?.mpId)}>
              <SelectIdentity
                multiple
                multipleSelectable={multiple}
                createButton={props => (
                  <Box>
                    <IconButton {...props} sx={{ width: '100%' }}>
                      <AddIcon />
                    </IconButton>
                  </Box>
                )}
                {...field}
                value={value}
                placeholder={placeholder}
                emptyState={<Trans id="No meeting participants found" />}
                juridicalKinds={[JuridicalKind.Person]}
                handleCreate={async identity => {
                  const mp = await doCreateMp({
                    variables: {
                      companyId: meeting.companyId,
                      input: {
                        present: true,
                        kind: GqlMpKind.Guest,
                        isShareholder: false,
                        meetingId: meeting.id,
                        adjuster: kind === FunctionKind.Adjuster,
                        chairman: kind === FunctionKind.Chairman,
                        secretary: kind === FunctionKind.Secretary,
                        identityInput: identityToInput(identity),
                      },
                    },
                  });
                  if (mp.data?.createMeetingParticipant.id) {
                    field.onChange(mp.data.createMeetingParticipant.id);
                  }
                }}
                onChange={async (_e, value) => {
                  if (value) {
                    field.onChange(value);
                    await onSubmit({ mpId: value as string | string[] });
                  }
                }}
                onDeselect={onDeselect}
                identities={createRoleOptions(meeting.meetingParticipants)}
              />
            </FormControl>
          )}
        />
      </form>
    </Stack>
  );
};
